import React from 'react';
import { StatCard } from '@/components/shared';
import type { AreaBroadcastRow } from '@/pages/Admin/area-notifications/api';

interface AreaBroadcastsStatsProps {
  items: AreaBroadcastRow[];
  loading: boolean;
}

function computeAggregates(items: AreaBroadcastRow[]) {
  // Only completed broadcasts count toward delivered/opens — queued ones
  // haven't actually been sent and failed ones never will. We still count
  // every broadcast in the "Broadcasts" tile so admins see total activity.
  let broadcasts = 0;
  let audience = 0;
  let delivered = 0;
  let opens = 0;

  for (const b of items) {
    broadcasts += 1;
    if (b.status === 'completed') {
      audience += b.audience_count ?? 0;
      delivered += b.delivered_count ?? 0;
      opens += b.opened_count ?? 0;
    }
  }

  const openRate = delivered > 0 ? (opens / delivered) * 100 : 0;
  return { broadcasts, audience, delivered, opens, openRate };
}

function AreaBroadcastsStats({ items, loading }: AreaBroadcastsStatsProps): React.ReactElement {
  const { broadcasts, audience, opens, openRate } = computeAggregates(items);

  return (
    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
      <StatCard
        variant="dense"
        label="Broadcasts"
        value={loading ? '—' : broadcasts.toLocaleString()}
        accent="violet"
        pending={loading}
        icon={
          <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="1.5">
            <path strokeLinecap="round" strokeLinejoin="round" d="M10.34 15.84c-.688-.06-1.386-.09-2.09-.09H7.5a4.5 4.5 0 110-9h.75c.704 0 1.402-.03 2.09-.09m0 9.18c.253.962.584 1.892.985 2.783.247.55.06 1.21-.463 1.511l-.657.38c-.551.318-1.26.117-1.527-.461a20.845 20.845 0 01-1.44-4.282m3.102.069a18.03 18.03 0 01-.59-4.59c0-1.586.205-3.124.59-4.59m0 9.18a23.848 23.848 0 018.835 2.535M10.34 6.66a23.847 23.847 0 008.835-2.535m0 0A23.74 23.74 0 0018.795 3m.38 1.125a23.91 23.91 0 011.014 5.395m-1.014 8.855c-.118.38-.245.754-.38 1.125m.38-1.125a23.91 23.91 0 001.014-5.395m0-3.46c.495.413.811 1.035.811 1.73 0 .695-.316 1.317-.811 1.73m0-3.46a24.347 24.347 0 010 3.46" />
          </svg>
        }
      />
      <StatCard
        variant="dense"
        label="Audience"
        value={loading ? '—' : audience.toLocaleString()}
        accent="cyan"
        pending={loading}
        icon={
          <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="1.5">
            <path strokeLinecap="round" strokeLinejoin="round" d="M15 19.128a9.38 9.38 0 002.625.372 9.337 9.337 0 004.121-.952 4.125 4.125 0 00-7.533-2.493M15 19.128v-.003c0-1.113-.285-2.16-.786-3.07M15 19.128v.106A12.318 12.318 0 018.624 21c-2.331 0-4.512-.645-6.374-1.766l-.001-.109a6.375 6.375 0 0111.964-3.07M12 6.375a3.375 3.375 0 11-6.75 0 3.375 3.375 0 016.75 0zm8.25 2.25a2.625 2.625 0 11-5.25 0 2.625 2.625 0 015.25 0z" />
          </svg>
        }
      />
      <StatCard
        variant="dense"
        label="Opens"
        value={loading ? '—' : opens.toLocaleString()}
        accent="blue"
        pending={loading}
        icon={
          <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="1.5">
            <path strokeLinecap="round" strokeLinejoin="round" d="M21.75 9v.906a2.25 2.25 0 01-1.183 1.981l-6.478 3.488M2.25 9v.906a2.25 2.25 0 001.183 1.981l6.478 3.488m8.839 2.51l-4.66-2.51m0 0l-1.023-.55a2.25 2.25 0 00-2.134 0l-1.022.55m0 0l-4.661 2.51m16.5 1.615a2.25 2.25 0 01-2.25 2.25h-15a2.25 2.25 0 01-2.25-2.25V8.844a2.25 2.25 0 011.183-1.981l7.5-4.039a2.25 2.25 0 012.134 0l7.5 4.039a2.25 2.25 0 011.183 1.98V19.5z" />
          </svg>
        }
      />
      <StatCard
        variant="dense"
        label="Open Rate"
        value={loading ? '—' : `${openRate.toFixed(1)}%`}
        accent="pink"
        pending={loading}
        icon={
          <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="1.5">
            <path strokeLinecap="round" strokeLinejoin="round" d="M10.5 6a7.5 7.5 0 107.5 7.5h-7.5V6z" />
            <path strokeLinecap="round" strokeLinejoin="round" d="M13.5 10.5H21A7.5 7.5 0 0013.5 3v7.5z" />
          </svg>
        }
      />
    </div>
  );
}

export default AreaBroadcastsStats;
